import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router";
import moment from "moment";
import { resetAllQueryResult } from "../../state/queryStore";
import { QueryList } from "../../common/constants/helpers";
import { RootState } from "../../store";
import { exportExcel } from "../../common/constants/exportExcel";
import CleanButton from "../../common/components/CleanButton";
import { Tooltips } from "../../common/constants/Tooltips";
import { setActiveCaseNumber, updateCaseMaster } from "../../state/globalStore";
import { buildSummaryRequest, civilRequest } from "../../state/connect-api";
import { LookupRequest } from "../../models/lookup-request";
import { useCivilCaseLookupMutation, useGetCaseSummaryMutation } from "../../state/globalApi";

const useQueryList = () => {
  const [columns, setcolumns] = useState([]);
  const [data, setData] = useState([]);
  const [visibility, setVisibility] = useState<boolean>(false);
  const history = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { allQueryResult } = useSelector((state: RootState) => state.queryApi);
  const [getCaseSummary] = useGetCaseSummaryMutation();
  const [civilLookupCase] = useCivilCaseLookupMutation();

  const handleSelectTRCRCase = async (caseNumber: string) => {
    dispatch(setActiveCaseNumber(caseNumber));
    const request: LookupRequest = buildSummaryRequest(caseNumber);
    const results = await getCaseSummary(request).unwrap();
    dispatch(updateCaseMaster({ summary: results }));
    history("/lookup/detail", { replace: true, state: { fromPage: location.state } });
    return;
  };

  const handleSelectCivilCase = async (val) => {
    const lookupRequest: LookupRequest = civilRequest("CASENUMBER", val, "01-01-1753", moment(new Date()).format("MM-DD-YYYY"));
    localStorage.setItem("caseNumber", val);
    const results = await civilLookupCase(lookupRequest).unwrap();
    dispatch(setActiveCaseNumber(results?.LIST[0]?.CasePK));
    history("/civil/civil-detail", { replace: true, state: { fromPage: location.state } });
    return;
  };

  const trCrDailyColumns = [
    { key: "agency", title: "Agency", dataIndex: "AGENCY" },
    { key: "ArrestingOfficer", title: "Arresting Officer", dataIndex: "ARRESTING_OFFICER" },
    { key: "attorney", title: "Attorney", dataIndex: "ATTORNEY" },
    { key: "case", title: "Case", dataIndex: "CASE" },
    { key: "date", title: "Date", dataIndex: "DATE" },
    { key: "judge", title: "Judge", dataIndex: "JUDGE" },
    { key: "name", title: "Name", dataIndex: "NAME" },
    { key: "prosecutor", title: "Prosecutor", dataIndex: "PROSECUTOR" },
    { key: "room", title: "Room", dataIndex: "ROOM" },
    { key: "status", title: "Status", dataIndex: "STATUS" },
    { key: "time", title: "Time", dataIndex: "TIME" },
    { key: "violations", title: "Violations", dataIndex: "VIOLATIONS" },
    {
      key: "actions",
      dataIndex: "CASE",
      title: "Actions",
      render: (caseNumber: string) => <CleanButton icon="eye" type="link" tooltip={Tooltips.View} onClick={() => handleSelectTRCRCase(caseNumber)} />
    }
  ];

  const trCrArraignmentColumns = [
    { key: "agency", title: "Agency", dataIndex: "AGENCY" },
    { key: "judge", title: "Judge", dataIndex: "JUDGE" },
    { key: "name", title: "Name", dataIndex: "NAME" },
    { key: "room", title: "Room", dataIndex: "ROOM" },
    { key: "case", title: "Case", dataIndex: "CASE" },
    { key: "date", title: "Date", dataIndex: "DATE" },
    { key: "time", title: "Time", dataIndex: "TIME" },
    { key: "violations", title: "Violations", dataIndex: "VIOLATIONS" },
    {
      key: "actions",
      dataIndex: "CASE",
      title: "Actions",
      render: (caseNumber: string) => <CleanButton icon="eye" type="link" tooltip={Tooltips.View} onClick={() => handleSelectTRCRCase(caseNumber)} />
    }
  ];

  const trCrAttorneyColumns = [
    { key: "attorney", title: "Attorney", dataIndex: "ATTORNEY" },
    { key: "case", title: "Case", dataIndex: "CASE" },
    { key: "date", title: "Date", dataIndex: "DATE" },
    { key: "name", title: "Name", dataIndex: "NAME" },
    { key: "status", title: "Status", dataIndex: "STATUS" },
    { key: "time", title: "Time", dataIndex: "TIME" },
    { key: "violations", title: "Violations", dataIndex: "VIOLATIONS" },
    {
      key: "actions",
      dataIndex: "CASE",
      title: "Actions",
      render: (caseNumber: string) => <CleanButton icon="eye" type="link" tooltip={Tooltips.View} onClick={() => handleSelectTRCRCase(caseNumber)} />
    }
  ];

  const trCrAgencyColumns = [
    { key: "agency", title: "Agency", dataIndex: "Agency" },
    { key: "ArrestingOfficer", title: "Arresting Officer", dataIndex: "OFFICER" },
    { key: "case", title: "Case", dataIndex: "CASE" },
    { key: "date", title: "Date", dataIndex: "DATE" },
    { key: "time", title: "Time", dataIndex: "TIME" },
    { key: "violations", title: "Violations", dataIndex: "VIOLATIONS" },
    {
      key: "actions",
      dataIndex: "CASE",
      title: "Actions",
      render: (caseNumber: string) => <CleanButton icon="eye" type="link" tooltip={Tooltips.View} onClick={() => handleSelectTRCRCase(caseNumber)} />
    }
  ];

  const civilDailyQueryColumns = [
    { key: "judgeName", title: "Judge Name", dataIndex: "JudgeName" },
    { key: "caseNumber", title: "Case Number", dataIndex: "CaseNumber" },
    { key: "dateTime", title: "Next Date Time", dataIndex: "CaseStatusItemNextDateTime" },
    { key: "description", title: "Description", dataIndex: "Description" },
    { key: "plaintiffList", title: "Plaintiff List", dataIndex: "PlaintiffList", render: (val) => val.replaceAll("<br />", ",") },
    { key: "defendantList", title: "Defendant List", dataIndex: "DefendantList", render: (val) => val.replaceAll("<br />", ",") },
    {
      key: "actions",
      dataIndex: "CaseNumber",
      title: "Actions",
      render: (caseNumber: string) => <CleanButton icon="eye" type="link" tooltip={Tooltips.View} onClick={() => handleSelectCivilCase(caseNumber)} />
    }
  ];

  const civilAttorneyQueryColumns = [
    { key: "attorneyName", title: "Attorney Name", dataIndex: "AttorneyName" },
    { key: "caseNumber", title: "Case Number", dataIndex: "CaseNumber" },
    { key: "dateTime", title: "Next Date Time", dataIndex: "CaseStatusItemNextDateTime" },
    { key: "description", title: "Description", dataIndex: "Description" },
    { key: "plaintiffList", title: "Plaintiff List", dataIndex: "PlaintiffList", render: (val) => val.replaceAll("<br />", ",") },
    { key: "defendantList", title: "Defendant List", dataIndex: "DefendantList", render: (val) => val.replaceAll("<br />", ",") },
    {
      key: "actions",
      dataIndex: "CaseNumber",
      title: "Actions",
      render: (caseNumber: string) => <CleanButton icon="eye" type="link" tooltip={Tooltips.View} onClick={() => handleSelectCivilCase(caseNumber)} />
    }
  ];

  useEffect(() => {
    setData(allQueryResult?.LIST);
    if (location.state) {
      switch (location.state) {
        case QueryList.TRCRDaily:
          setcolumns(trCrDailyColumns);
          break;
        case QueryList.TRCRArraignment:
          setcolumns(trCrArraignmentColumns);
          break;
        case QueryList.TRCRAttorney:
          setcolumns(trCrAttorneyColumns);
          break;
        case QueryList.TRCRAgency:
          setcolumns(trCrAgencyColumns);
          break;
        case QueryList.CivilDaily:
          setcolumns(civilDailyQueryColumns);
          break;
        case QueryList.CivilAttorney:
          setcolumns(civilAttorneyQueryColumns);
          break;
      }
    }
  }, [location.state]);

  const handleNewQuery = () => {
    dispatch(resetAllQueryResult());
    switch (location.state) {
      case QueryList.TRCRDaily:
        history("/tr-cr-query/daily-query");
        break;
      case QueryList.TRCRArraignment:
        history("/tr-cr-query/arraignment-query");
        break;
      case QueryList.TRCRAttorney:
        history("/tr-cr-query/attorney-query");
        break;
      case QueryList.TRCRAgency:
        history("/tr-cr-query/agency-query");
        break;
      case QueryList.CivilDaily:
        history("/civil-query/daily-query");
        break;
      case QueryList.CivilAttorney:
        history("/civil-query/attorney-query");
        break;
    }
  };

  const handleOnExportResult = () => {
    exportExcel(allQueryResult?.LIST, location.state);
  };

  const handleViewPDFModal = () => {
    setVisibility(true);
  };

  return {
    columns,
    data,
    handleNewQuery,
    handleOnExportResult,
    handleViewPDFModal,
    visibility,
    setVisibility,
    allQueryResult,
    fromPage: location.state
  };
};

export default useQueryList;
